import { useCallback, useEffect, useMemo, useState } from 'react';
import { IServerColumnConfig } from '@/components/table/column-config';
import {
	deleteStyle,
	fetchActiveStyles,
	fetchSingleStyle,
	fetchStyles,
	fetchStylesTableConfig,
	postStyle,
	putStyle
} from './actions';
import {
	GeometrieTip,
	GeometryStylesReturnType,
	IGeometryStyle,
	IGeometryStylePayload
} from './types';

export function useGeometryStyles(
	getOnRender: boolean | undefined = undefined,
	queryParam: string | undefined = undefined,
	itemsSet = 10,
	/**
	 * Paginarea INIȚIALĂ (de regulă cea din URL, via useListQueryState). Fără ea,
	 * starea internă pornește pe 0/10 și primul fetch pleacă cu valori greșite, urmat
	 * de un al doilea după sincronizarea din efectele-punte — două call-uri și un race
	 * pe răspunsuri. Doar valoarea de la primul render contează.
	 */
	initialPagination: { page: number; items: number } | undefined = undefined,
	withTableConfig = false
): GeometryStylesReturnType {
	const [data, setData] = useState<IGeometryStyle[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	// Configurația de coloane (api/geometry-styles/table-config) — cerută doar de pagina
	// de listă (withTableConfig); la eroare rămâne null și tabelul cade silențios pe
	// coloanele definite local.
	const [tableConfig, setTableConfig] = useState<IServerColumnConfig[] | null>(null);
	const [loadingTableConfig, setLoadingTableConfig] = useState<boolean>(
		Boolean(withTableConfig)
	);
	const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
	const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
	const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
	const [enableQuery, setEnableQuery] = useState<boolean>(false);
	const [page, setPage] = useState<number>(initialPagination?.page ?? 0);
	const [items, setItems] = useState<number>(initialPagination?.items ?? itemsSet);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(0);

	const handleResponse = (pagesParam: number, itemsParam: number): void => {
		setTotalPages(pagesParam);
		setTotalItems(itemsParam);
	};

	useEffect(() => {
		if (!withTableConfig) return undefined;
		let active = true;
		setLoadingTableConfig(true);
		fetchStylesTableConfig()
			.then((columns) => {
				if (active) setTableConfig(columns.length ? columns : null);
			})
			.catch(() => {
				// Fallback silențios: fără config BE, tabelul folosește coloanele locale.
				if (active) setTableConfig(null);
			})
			.finally(() => {
				if (active) setLoadingTableConfig(false);
			});
		return () => {
			active = false;
		};
	}, [withTableConfig]);

	const getStyles = useCallback(async (pageParam: number, itemsParam: number) => {
		try {
			setLoading(true);
			const response = await fetchStyles('', pageParam, itemsParam, handleResponse);
			setData(response);
		} catch {
			// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
		} finally {
			setLoading(false);
		}
	}, []);

	const getStylesQuery = useCallback(
		async (query: string, pageParam: number, itemsParam: number) => {
			try {
				if (!enableQuery) setEnableQuery(true);
				setLoading(true);
				const response = await fetchStyles(query, pageParam, itemsParam, handleResponse);
				setData(response);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		[enableQuery]
	);

	const getStyle = useCallback(async (styleId: number | string) => {
		try {
			return await fetchSingleStyle(styleId);
		} catch {
			return null;
		}
	}, []);

	const createStyle = useCallback(
		async (payload: IGeometryStylePayload, onSuccess?: () => void) => {
			try {
				setLoadingCreate(true);
				const response = await postStyle(payload);
				if (response === 200) {
					invalidateActiveStyles();
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCreate(false);
			}
		},
		[]
	);

	const editStyle = useCallback(
		async (
			styleId: number | string,
			payload: IGeometryStylePayload,
			onSuccess?: () => void
		) => {
			try {
				setLoadingUpdate(true);
				const response = await putStyle(styleId, payload);
				if (response === 200) {
					invalidateActiveStyles();
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingUpdate(false);
			}
		},
		[]
	);

	const removeStyle = useCallback(
		async (styleId: number | string, onSuccess?: () => void) => {
			try {
				setLoadingDelete(true);
				const response = await deleteStyle(styleId);
				if (typeof response === 'number' && response >= 200 && response < 300) {
					invalidateActiveStyles();
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingDelete(false);
			}
		},
		[]
	);

	useEffect(() => {
		if (getOnRender && queryParam === undefined) {
			getStyles(page, items);
		}
	}, [getOnRender, page, items]);

	useEffect(() => {
		if (queryParam !== undefined) {
			getStylesQuery(queryParam, page, items);
		} else if (enableQuery) {
			getStyles(page, items);
		}
	}, [queryParam, page, items]);

	return {
		data,
		loading,
		tableConfig,
		loadingTableConfig,
		loadingCreate,
		loadingUpdate,
		loadingDelete,
		getStyles,
		getStylesQuery,
		getStyle,
		createStyle,
		editStyle,
		removeStyle,
		page,
		items,
		setPage,
		setItems,
		totalPages,
		totalItems
	};
}

// ---- stilurile active pentru hărți (cel mult un stil activ per tip de geometrie) ----

export type ActiveStylesByTip = Partial<Record<GeometrieTip, IGeometryStyle>>;

// Cache PARTAJAT la nivel de modul: toate hărțile aplicației refolosesc UN singur fetch.
// Înainte, fiecare MapView refăcea fetch-ul la montare și trecea prin starea goală, iar dacă
// primul fetch eșua nu mai reîncerca. Pagina Proiecte e de regulă PRIMA hartă montată
// (cold-start, adesea imediat după login), așa că un fetch tranzitoriu eșuat acolo lăsa
// geometriile permanent pe culorile fallback (fără simbologia din nomenclator) — exact bug-ul
// raportat, care nu apărea pe detaliu/editor (deschise mai târziu, cu totul „cald"). Cache-ul
// partajat + retry-ul de mai jos elimină atât golirea per-montare, cât și eșecul fără reîncercare.
let activeStylesCache: IGeometryStyle[] | null = null;
let activeStylesPromise: Promise<IGeometryStyle[]> | null = null;
const activeStylesSubscribers = new Set<(styles: IGeometryStyle[]) => void>();

const wait = (ms: number): Promise<void> =>
	new Promise((resolve) => {
		setTimeout(resolve, ms);
	});

// Reîncearcă la EROARE (tranzitorie la cold-start), cu backoff scurt; un răspuns reușit —
// chiar și gol (niciun stil activ) — e o stare validă care se cache-uiește (fallback corect).
async function loadActiveStyles(): Promise<IGeometryStyle[]> {
	for (let attempt = 0; attempt < 4; attempt += 1) {
		try {
			// eslint-disable-next-line no-await-in-loop
			const styles = await fetchActiveStyles();
			activeStylesCache = styles;
			activeStylesSubscribers.forEach((cb) => cb(styles));
			return styles;
		} catch {
			// eslint-disable-next-line no-await-in-loop
			await wait(400 * (attempt + 1));
		}
	}
	return []; // după reîncercări eșuate cădem pe fallback, FĂRĂ a cache-ui (o cerere ulterioară mai poate încerca)
}

function ensureActiveStyles(): Promise<IGeometryStyle[]> {
	if (activeStylesCache) return Promise.resolve(activeStylesCache);
	if (!activeStylesPromise) {
		activeStylesPromise = loadActiveStyles().finally(() => {
			activeStylesPromise = null;
		});
	}
	return activeStylesPromise;
}

// Invalidează cache-ul partajat după o mutație de stil (create/edit/delete) și reîncarcă
// dacă vreo hartă e montată, ca simbologia să se actualizeze imediat, fără reload de pagină.
function invalidateActiveStyles(): void {
	activeStylesCache = null;
	activeStylesPromise = null;
	if (activeStylesSubscribers.size > 0) ensureActiveStyles().catch(() => undefined);
}

export function useActiveGeometryStyles(): {
	stylesByTip: ActiveStylesByTip;
	loading: boolean;
} {
	// dacă stilurile s-au încărcat deja (altă hartă), le folosim imediat — fără flash de fallback
	const [styles, setStyles] = useState<IGeometryStyle[]>(activeStylesCache ?? []);
	const [loading, setLoading] = useState<boolean>(activeStylesCache == null);

	useEffect(() => {
		let alive = true;
		const onStyles = (next: IGeometryStyle[]): void => {
			if (alive) setStyles(next);
		};
		activeStylesSubscribers.add(onStyles);
		ensureActiveStyles()
			.then((next) => {
				if (alive) setStyles(next);
			})
			.finally(() => {
				if (alive) setLoading(false);
			});
		return () => {
			alive = false;
			activeStylesSubscribers.delete(onStyles);
		};
	}, []);

	const stylesByTip = useMemo<ActiveStylesByTip>(() => {
		const byTip: ActiveStylesByTip = {};
		styles.forEach((s) => {
			byTip[s.type] = s;
		});
		return byTip;
	}, [styles]);

	return { stylesByTip, loading };
}
